import { BidderAdapter } from '@pubstack/common/src/adm/bidder-adapter'
import { AdmRefreshGlobalSettings, AdmRefreshGlobalSettingsData } from '@pubstack/common/src/adm/refresh-global-settings'
import { AdmRuleHistory, RefreshRule } from '@pubstack/common/src/adm/rules'
import { AdUnit, AdUnitsImportValidationResults } from '@pubstack/common/src/adunit'
import { ContextKey } from '@pubstack/common/src/contextKey'
import { GamConfig } from '@pubstack/common/src/gam/gamConfig'
import { LineItemTypeName } from '@pubstack/common/src/gam/lineItemType'
import { NativeTemplate } from '@pubstack/common/src/nativeTemplate'
import { PrebidConfig } from '@pubstack/common/src/prebidConfig'
import { FullPrebidModule, MultipleEditPayload, PrebidModuleData, PrebidModuleDataBySite } from '@pubstack/common/src/prebidModule'
import { AdmSiteConfig } from '@pubstack/common/src/siteConfig'
import { sortByName } from '@pubstack/common/src/sort'
import { Stack } from '@pubstack/common/src/stack'
import { TamConfig } from '@pubstack/common/src/tam/tamConfig'
import { useCallback } from 'react'
import { useFetchWithException } from './api-access'

export const useAdUnits = (deps: unknown[] | null = []) => {
  const fetch = useFetchWithException<AdUnit>(`/adm/adunits`, {}, deps || undefined)
  const validate = useFetchWithException<boolean>(`/adm/adunits/validate`, {}, deps || undefined)
  const fetchAll = useFetchWithException<AdUnit[]>(`/adm/adunits`, {}, deps || undefined)
  const validateImports = useFetchWithException<AdUnitsImportValidationResults>(`/adm/adunits/files/validate`, {}, deps || undefined)
  const uploadImports = useFetchWithException<void>(`/adm/adunits/files`, {}, deps || undefined)

  const getById = (id: AdUnit['id']) => fetch.get(id)
  const put = (adUnit: AdUnit) => fetch.put(adUnit.id, adUnit)
  const validateName = (adUnitName: string) => validate.get(`?name=${encodeURIComponent(adUnitName)}`)

  const validateImportFile = useCallback(
    (file: File) => {
      const data = new FormData()
      data.append('csv', file)
      return validateImports.post('', data)
    },
    [validateImports]
  )

  const uploadImportFile = useCallback(
    (file: File) => {
      const data = new FormData()
      data.append('csv', file)
      return uploadImports.post('', data)
    },
    [uploadImports]
  )

  return { all: { ...fetchAll }, byId: { ...fetch, get: getById, put }, validateName, validateImportFile, uploadImportFile }
}

export const useContextKeys = (deps: unknown[] | null = []) => {
  const fetch = useFetchWithException<ContextKey>(`/adm/context-keys`, {}, deps || undefined)
  const validate = useFetchWithException<boolean>(`/adm/context-keys/validate`, {}, deps || undefined)
  const fetchAll = useFetchWithException<ContextKey[]>(`/adm/context-keys`, {}, deps || undefined)
  const getAll = async () => sortByName(await fetchAll.get())
  const put = (contextKey: ContextKey) => fetch.put(contextKey)
  const getByName = (name: ContextKey['name']) => fetch.get(name)
  const validateName = (contextKeyName: string) => validate.get(contextKeyName)
  return { all: { ...fetchAll, get: getAll }, byId: { ...fetch, put, get: getByName }, validateName }
}

export const useGamConfig = (deps: unknown[] | null = []) => {
  const fetch = useFetchWithException<GamConfig>(`/adm/gam-config`, {}, deps || undefined)
  const putNetworkId = (networkId: string) => fetch.put(networkId)
  return { byId: { ...fetch, putNetworkId } }
}

export const useGamSiteConfig = (deps: unknown[] | null = []) => {
  const fetch = useFetchWithException<AdmSiteConfig>(`/adm/gam-config/site-config`, {}, deps || undefined)
  const putSiteConfig = (siteConfig: AdmSiteConfig) => fetch.put(siteConfig.siteId, siteConfig)
  return { byId: { ...fetch, put: putSiteConfig } }
}

export const usePrebidConfig = (deps: unknown[] | null = []) => {
  const fetch = useFetchWithException<PrebidConfig>(`/adm/prebid-config`, {}, deps || undefined)
  const putApsAccountId = (apsAccountId: string) => fetch.put('/aps-account-id', { apsAccountId })
  return { prebidConfig: { loading: fetch.loading, get: fetch.get, putApsAccountId } }
}

export const usePrebidSiteConfig = (deps: unknown[] | null = []) => {
  const fetch = useFetchWithException<AdmSiteConfig>('/adm/prebid-config/site-config', {}, deps || undefined)
  const putSiteConfig = (siteConfig: AdmSiteConfig) => fetch.put(siteConfig.siteId, siteConfig)
  return { prebidSiteConfig: { loading: fetch.loading, put: putSiteConfig } }
}

export const usePrebidModulesConfig = (deps: unknown[] | null = []) => {
  const fetch = useFetchWithException<FullPrebidModule[]>('/adm/prebid-config/modules', {}, deps || undefined)
  const get = (siteId: string) => fetch.get(`/${siteId}`)
  return { prebidModulesConfig: { get, loading: fetch.loading } }
}

export const useIdentityModule = (deps: unknown[] | null = []) => {
  const fetchAll = useFetchWithException<PrebidModuleDataBySite<'Identity'>[]>(`/adm/catalog/identity`, {}, deps || undefined)
  const fetch = useFetchWithException<PrebidModuleDataBySite<'Identity'>>(`/adm/catalog/identity`, {}, deps || undefined)
  const put = (id: string, siteId: string, data: PrebidModuleData<'Identity'>, enabled: boolean) => fetch.put(`/${id}/${siteId}?enabled=${enabled}`, data)
  const get = (id: string) => fetchAll.get(`/${id}`)
  const multipleEditFetch = useFetchWithException<MultipleEditPayload<'Identity'>>(`/adm/catalog/identity/multipleEdit`, {}, deps || undefined)
  const multipleEdit = (id: string, payload: MultipleEditPayload<'Identity'>) => multipleEditFetch.put(`/${id}`, payload)
  return { all: { ...fetchAll, get }, byId: { ...fetch, put, multipleEdit } }
}

export const useAnalyticsModule = (deps: unknown[] | null = []) => {
  const fetchAll = useFetchWithException<PrebidModuleDataBySite<'Analytics'>[]>(`/adm/catalog/analytics`, {}, deps || undefined)
  const fetch = useFetchWithException<PrebidModuleDataBySite<'Analytics'>>(`/adm/catalog/analytics`, {}, deps || undefined)
  const put = (id: string, siteId: string, data: PrebidModuleData<'Analytics'>, enabled: boolean) => fetch.put(`/${id}/${siteId}?enabled=${enabled}`, data)
  const get = (id: string) => fetchAll.get(`/${id}`)
  const multipleEditFetch = useFetchWithException<MultipleEditPayload<'Analytics'>>(`/adm/catalog/analytics/multipleEdit`, {}, deps || undefined)
  const multipleEdit = (id: string, payload: MultipleEditPayload<'Analytics'>) => multipleEditFetch.put(`/${id}`, payload)
  return { all: { ...fetchAll, get }, byId: { ...fetch, put, multipleEdit } }
}

export const useRealTimeDataModule = (deps: unknown[] | null = []) => {
  const fetchAll = useFetchWithException<PrebidModuleDataBySite<'RTD'>[]>(`/adm/catalog/real-time-data`, {}, deps || undefined)
  const fetch = useFetchWithException<PrebidModuleDataBySite<'RTD'>>(`/adm/catalog/real-time-data`, {}, deps || undefined)
  const put = (id: string, siteId: string, data: PrebidModuleData<'RTD'>, enabled: boolean) => fetch.put(`/${id}?siteId=${siteId}&enabled=${enabled}`, data)
  const get = (id: string) => fetchAll.get(`/${id}`)
  const multipleEditFetch = useFetchWithException<MultipleEditPayload<'RTD'>>(`/adm/catalog/real-time-data/multipleEdit`, {}, deps || undefined)
  const multipleEdit = (id: string, payload: MultipleEditPayload<'RTD'>) => multipleEditFetch.put(`/${id}`, payload)
  return { all: { ...fetchAll, get }, byId: { ...fetch, put, multipleEdit } }
}

export const useTamConfig = (deps: unknown[] | null = []) => {
  const fetch = useFetchWithException<TamConfig>(`/adm/tam-config`, {}, deps || undefined)
  const putPubId = (publisherId: TamConfig) => fetch.put(publisherId)
  return { byId: { ...fetch, putPubId } }
}

export const useTamSiteConfig = (deps: unknown[] | null = []) => {
  const fetch = useFetchWithException<AdmSiteConfig>(`/adm/tam-config/site-config`, {}, deps || undefined)
  const putSiteConfig = (siteConfig: AdmSiteConfig) => fetch.put(siteConfig.siteId, siteConfig)
  return { byId: { ...fetch, put: putSiteConfig } }
}

export const useBidderAdapters = (deps: unknown[] | null = []) => {
  const fetchAll = useFetchWithException<BidderAdapter[]>('/adm/bidder-adapters', {}, deps || undefined)
  return { all: { ...fetchAll } }
}

export const useRefreshRules = (deps: unknown[] | null = []) => {
  const fetchAll = useFetchWithException<RefreshRule[]>(`/adm/refresh-rules`, deps || undefined)
  const getAll = (withStacksUse?: boolean) => fetchAll.get(`?withStacksUse=${withStacksUse}`)
  const fetch = useFetchWithException<RefreshRule>(`/adm/refresh-rules`, deps || undefined)
  const loadExcludedCampaignsAction = useFetchWithException<void>(`/adm/refresh-rules`, {}, deps || undefined)
  const loadExcludedCampaigns = () => loadExcludedCampaignsAction.post('/global-settings/load-excluded-campaigns')
  const fetchGlobalSettings = useFetchWithException<AdmRefreshGlobalSettingsData>(`/adm/refresh-rules/global-settings`, {}, deps || undefined)
  const saveGlobalSettings = useFetchWithException<AdmRefreshGlobalSettings>(`/adm/refresh-rules/global-settings`, {}, deps || undefined)
  const saveExclusionLineItemTypes = useFetchWithException<LineItemTypeName[]>(`/adm/refresh-rules/global-settings/excluded-line-item-types`, {}, deps || undefined)
  const fetchAllWithHistory = useFetchWithException<AdmRuleHistory>(`/adm/refresh-rules/history`, deps || undefined)
  return {
    all: { ...fetchAll, getAllWithStacks: getAll },
    byId: { ...fetch },
    allWithHistory: { ...fetchAllWithHistory },
    loadExcludedCampaigns,
    fetchGlobalSettings,
    saveGlobalSettings,
    saveExclusionLineItemTypes,
  }
}

export const useStacks = (deps: unknown[] | null = []) => {
  const fetchAll = useFetchWithException<Stack[]>(`/adm/stacks`, {}, deps || undefined)
  const fetch = useFetchWithException<Stack>(`/adm/stacks`, {}, deps || undefined)
  const getById = (id: Stack['id']) => fetch.get(id)
  return { all: { ...fetchAll }, byId: { ...fetch, getById } }
}

export const useNative = (deps: unknown[] | null = []) => {
  const templates = useFetchWithException<NativeTemplate[]>('/adm/native/templates', deps || undefined)

  return { template: { all: templates.get } }
}
